'use client'

import { m } from 'framer-motion'
import { useMemo } from 'react'

import AudienceAccordion, { type AudienceItem } from '@/components/sections/AudienceAccordion'

type ServicePreset = {
  id: string
  number: string
  title: string
  subtitle: string
  description: string
  tags: string[]
  focus?: string[]
  outcomes?: string[]
  pains?: string[]
  solutions?: string[]
  stats?: string
  statsLabel?: string
  implementationFormat?: string
  gradient?: string
}

const fallbackServices: ServicePreset[] = [
  {
    id: 'landing',
    number: '01',
    title: 'Лендинг',
    subtitle: 'Точечная продажа',
    description:
      'Быстрый формат запуска под конкретное предложение. Фокус на конверсии, заявках и понятном сценарии для клиента.',
    tags: ['Быстрый запуск', 'Высокая конверсия', 'Адаптив под мобильные', 'Интеграция аналитики'],
    gradient: 'from-blue-600 via-indigo-500 to-purple-500',
  },
  {
    id: 'corporate',
    number: '02',
    title: 'Корпоративный сайт',
    subtitle: 'Имидж и доверие',
    description:
      'Системная презентация компании, услуг и экспертизы. Усиливает бренд и формирует стабильный поток обращений.',
    tags: ['Индивидуальный дизайн', 'Управление через CMS', 'SEO-структура', 'Сценарии заявок'],
    gradient: 'from-violet-600 via-fuchsia-500 to-pink-500',
  },
  {
    id: 'ecommerce',
    number: '03',
    title: 'Интернет-магазин',
    subtitle: 'Продажи онлайн',
    description:
      'Полноценная e-commerce платформа: каталог, корзина, оплата, личный кабинет и бизнес-интеграции.',
    tags: ['Онлайн-оплата', 'Каталог и фильтры', 'Интеграции CRM/1С', 'Аналитика продаж'],
    gradient: 'from-pink-600 via-rose-500 to-orange-500',
  },
  {
    id: 'system',
    number: '04',
    title: 'Веб-платформа',
    subtitle: 'Сложные процессы',
    description:
      'Решения с индивидуальной логикой: личные кабинеты, сервисы и внутренние системы под масштабирование бизнеса.',
    tags: [
      'API и интеграции',
      'Надежная архитектура',
      'Сценарии высокой нагрузки',
      'Гибкое развитие продукта',
    ],
    gradient: 'from-cyan-600 via-teal-500 to-emerald-500',
  },
]

interface MaykopServicesProps {
  initialServices?: unknown[]
  sectionKicker?: string
  sectionTitle?: string
  sectionLead?: string
  leftTitle?: string
  rightTitle?: string
}

export default function MaykopServices({
  initialServices,
  sectionKicker = 'Под задачи бизнеса',
  sectionTitle = 'Кому это подходит',
  sectionLead = 'Выбирайте формат сайта под текущую цель: быстрый запуск, усиление бренда, рост продаж или автоматизацию процессов.',
  leftTitle = 'Проблема',
  rightTitle = 'Решение',
}: MaykopServicesProps) {
  const list = useMemo(() => {
    const source =
      initialServices && initialServices.length > 0 ? initialServices : fallbackServices

    return source.map((serviceRaw, index) => {
      const service =
        serviceRaw && typeof serviceRaw === 'object'
          ? (serviceRaw as Record<string, unknown>)
          : ({} as Record<string, unknown>)

      const safeTags = Array.isArray(service.tags)
        ? service.tags
            .filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0)
            .slice(0, 6)
        : []

      return {
        id: typeof service.id === 'string' ? service.id : `service-${index + 1}`,
        number:
          typeof service.number === 'string' ? service.number : String(index + 1).padStart(2, '0'),
        title:
          typeof service.title === 'string' && service.title.trim().length > 0
            ? service.title.trim()
            : `Услуга ${index + 1}`,
        subtitle:
          typeof service.subtitle === 'string' && service.subtitle.trim().length > 0
            ? service.subtitle.trim()
            : 'Формат проекта',
        description:
          typeof service.description === 'string' && service.description.trim().length > 0
            ? service.description.trim()
            : 'Подходящий формат запуска сайта под задачи бизнеса и план роста.',
        tags: safeTags,
        focus: Array.isArray(service.focus) ? service.focus : [],
        outcomes: Array.isArray(service.outcomes) ? service.outcomes : [],
        pains: Array.isArray(service.pains) ? service.pains : [],
        solutions: Array.isArray(service.solutions) ? service.solutions : [],
        stats: typeof service.stats === 'string' ? service.stats.trim() : '',
        statsLabel: typeof service.statsLabel === 'string' ? service.statsLabel.trim() : '',
        implementationFormat:
          typeof service.implementationFormat === 'string'
            ? service.implementationFormat.trim()
            : '',
        gradient:
          typeof service.gradient === 'string' && service.gradient.trim().length > 0
            ? service.gradient.trim()
            : 'from-blue-600 via-indigo-500 to-purple-500',
      }
    })
  }, [initialServices])

  const audienceItems = useMemo<AudienceItem[]>(
    () =>
      list.map((service) => {
        const splitIndex = Math.max(1, Math.ceil(service.tags.length / 2))
        const focus = service.tags.slice(0, splitIndex)
        const value = service.tags.slice(splitIndex)

        return {
          id: service.id,
          number: service.number,
          title: service.title,
          subtitle: service.subtitle,
          thesis: service.implementationFormat
            ? `${service.description}\n\nФормат реализации: ${service.implementationFormat}.`
            : service.description,
          color: service.gradient,
          description: undefined,
          stats: service.stats || 'Под ключ',
          statsLabel: service.statsLabel || 'формат реализации',
          pains:
            service.pains.length > 0
              ? service.pains
              : focus.length > 0
                ? focus
                : ['Структура проекта', 'План запуска', 'Сроки и приоритеты'],
          solutions:
            service.solutions.length > 0
              ? service.solutions
              : value.length > 0
                ? value
                : ['Прозрачная реализация', 'Контроль качества', 'Поддержка после запуска'],
        }
      }),
    [list],
  )

  return (
    <section className="relative overflow-hidden py-12 sm:py-16 md:py-24">
      <div className="home-container relative z-10">
        <div className="mb-8 sm:mb-10 lg:mb-14">
          <m.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2"
          >
            <span className="h-2 w-2 rounded-full bg-cyan-400" />
            <span className="home-caption text-gray-300">{sectionKicker}</span>
          </m.span>

          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="home-title mb-4"
          >
            {sectionTitle}
          </m.h2>

          <m.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.14 }}
            className="home-lead max-w-3xl text-gray-300"
          >
            {sectionLead}
          </m.p>
        </div>

        <AudienceAccordion items={audienceItems} leftTitle={leftTitle} rightTitle={rightTitle} />
      </div>
    </section>
  )
}
